/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { RenderConfig } from './RenderConfig';
import { Scene } from './Scene';
import { StageHelper } from './StageHelper';
import { Events } from '../interaction/Events';
import { InputController } from '../interaction/navigation/InputController';
import { Viewports } from './Viewports';
import { EventWatcher } from '../interaction/EventWatcher';
import { Controls } from '../interaction/navigation/Controls';
import { Cameras } from './Cameras';
import { Renderer } from './Renderer';
import { Objects } from '../objects/Objects';
/**
 * The Stage class is the top level container
 */
export class Stage {
    /**
     * Constructor
     * @param containerOrId id of the tag or the HTMLElement instance where the WebGLCanvas should be in
     * @param configuration additional configuration for the renderer
     * @param enableNavigator if true (default) the default navigation is enabled
     */
    constructor(containerOrId, desc = StageHelper.getDefaultSetup(), configuration) {
        this._cameras = new Cameras();
        this._scenes = new Map();
        this._lockRendering = false;
        this._actors = new Array();
        this._viewports = new Viewports();
        this._initRenderer(configuration);
        this._setupContainer(containerOrId);
        this._input = new InputController(this);
        this.setupStage(desc);
    }
    /**
     * Start / stop the current navigator
     * @param pause if true navigator is paused
     */
    pauseNavigator(pause) {
        this._controls.pauseNavigator(pause);
    }
    /**
     * locks the renderer so it can't render on each 'requestAnimationFrame' call
     * prevents new rendering before the scene is complete
     */
    lockRenderer() {
        this._lockRendering = true;
    }
    /**
     * unlocks the renderer so it can render on each 'requestAnimationFrame' call
     */
    unlockRenderer() {
        this._lockRendering = false;
    }
    /**
     * sets a hook to a function that will be called if the rendering is done
     * @param func function to call
     */
    setPostRenderHook(func) {
        this._postRenderHook = func;
    }
    /**
     * adds a new navigator factory to the controls
     * @param name name of the navigator (to refer to it e.g. in viewports)
     * @param factory the factory to create the navigator
     */
    addNavigatorFactory(name, factory) {
        this._controls.addNavigatorFactory(name, factory);
    }
    /**
     * changes an already added Navigator in a given viewport
     * @param name
     * @param viewportname
     */
    changeNavigator(name, viewportname = 'default') {
        this._viewports.ifPresentDo(viewportname, (vp) => {
            vp.navigator = name;
            this._controls.selectFor(this.getCamera(vp.camera), this._getSceneOrDefault(vp.scene), vp.navigator);
        });
    }
    /**
     * clears the object with the given id
     * @param id id of the object (taken from event or getObjectIdByName)
     */
    clearObject(id) {
        this._scenes.forEach((scene) => {
            const obj = scene.getObjectById(id);
            if (!!obj) {
                this._clearRecursive(obj);
                scene.remove(obj);
            }
        });
    }
    /**
     * clears a full group of objects tagged by 'tag'
     * @param tag the tag of the object group (used when adding the objects)
     */
    clearTaggedObjects(tag) {
        this._scenes.forEach((scene) => {
            scene.doOnTaggedObjects(tag, (obj) => {
                scene.remove(obj);
                this._clearRecursive(obj);
            });
            scene.removeTag(tag);
        });
    }
    /**
     * clears the content of all scenes, but not the scene itself
     */
    clearAllScenes() {
        this._scenes.forEach((scene) => this.clearScene(scene));
    }
    /**
     * clears a single scene content but not the scene itself
     * @param scene the scene name or instance of the scene to clear
     */
    clearScene(scene) {
        let curScn = null;
        if (typeof scene === 'string') {
            if (this._scenes.has(name)) {
                curScn = this._scenes.get(name);
            }
            else {
                // tslint:disable-next-line:no-console
                console.error('Cannot clear the Scene "' + scene + '"');
                return;
            }
        }
        else {
            curScn = scene;
        }
        while (curScn.children.length > 0) {
            this._clearRecursive(curScn.children[0]);
            curScn.remove(curScn.children[0]);
        }
    }
    /**
     * returns the object of an object with a given name
     * @param name name of the object
     * @param sceneName scene name where to look for the object (looks into the default scene if undefined)
     */
    getObjectByName(name, sceneName) {
        return this._getSceneOrDefault(sceneName).getObjectByName(name);
    }
    /**
     * returns the object id of an object with a given name
     * @param name name of the object
     * @param sceneName scene name where to look for the object (looks into the default scene if undefined)
     */
    getObjectIdByName(name, sceneName) {
        const obj = this.getObjectByName(name, sceneName);
        return (obj && obj.id) || -1;
    }
    /**
     * returns the inner WebGL rendering context
     * use this only if you are sure what you are doing
     */
    getWebGLContext() {
        return this._renderer.context;
    }
    /**
     * returns the inner renderer instance
     * use this only if you are sure what you are doing
     */
    getRenderer() {
        return this._renderer;
    }
    /**
     * activates a viewport (can also be done by mouseclick on a viewport)
     * @param name name of the viewport
     */
    activateViewport(name) {
        this._viewports.ifPresentDo(name, (vp) => {
            this._selectViewport(vp);
        });
    }
    /**
     * changes the settings of a Viewport
     * @param name name of the viewport to change
     * @param to new settings
     * @param duration number of seconds used for the transition
     */
    changeViewport(name, to, duration = 0) {
        this._viewports.ifPresentDo(name, (vp) => {
            vp.doTransition(this, to, duration);
        });
    }
    /**
     * changes the settings of a Camera
     * @param to new settings
     * @param duration number of seconds used for the transition
     */
    changeCamera(to, durationSec = 0) {
        this._cameras.doTransition(this, to, durationSec);
    }
    /**
     *
     * @param name Name of the camera or the viewport the camera is on
     * @param fromViewport if true the name is interpreted as viewport-name
     */
    getCameraDesc(name, fromViewport = false) {
        const camName = fromViewport ? this._viewports.getByName(name).camera : name;
        return this._cameras.getDesc(camName);
    }
    /**
     * sets the camera to the stage
     * @param name name of the camera in stage
     * @param camera the camera
     */
    setCamera(name, camera) {
        this._cameras.set(name, camera);
    }
    /**
     * returns a camera by name
     * @param name name of the camera
     */
    getCamera(name) {
        return this._cameras.get(name);
    }
    /**
     * calls the right capability for the given scene
     * Should only be called by Director!!!
     * @param func the capability function (any code to draw into the scene / or do manipulation)
     * @param object any data that enables the capability function to do the right thing
     * @param sceneName the name of the scene where the capability function may draw to
     * @returns success of the passed function
     */
    // tslint:disable-next-line:no-any
    applyCapability(func, object, sceneName) {
        return func(object, this._getSceneOrDefault(sceneName));
    }
    /**
     * calls a given function that gets the right scene to draw in
     * @param func function to call for drawing
     * @param sceneName name of the scene to draw in (if not set, default is used)
     * @returns boolean | void -> if the returntype is boolean it can be used as a forwarding from inner to outer function
     */
    applyThreeDirect(func, sceneName) {
        return func(this._getSceneOrDefault(sceneName));
    }
    /**
     * adds an actor to the stage
     * @param actor the actor
     */
    addActor(actor) {
        this._actors.push(actor);
    }
    /**
     * takes a screenshot of the current screen
     * @param type mime type of the image
     * @param encoderOpt see toDataURL
     */
    takeScreenshot(type = 'image/png', encoderOpt = 1.0) {
        this._render();
        return this._renderer.domElement.toDataURL(type, encoderOpt);
    }
    /**
     * starts the davinci glow render loop
     * its essential to call this, if you like to see something
     */
    start() {
        this._loadDefaultsIfRequired();
        this._animate();
    }
    /**
     * returns the event watcher (to detect how many listeners are registered for our events)
     */
    getEventWatcher() {
        return this._eventWatcher;
    }
    /**
     * fires an custom event to the container
     */
    dispatchEvent(what, object) {
        const event = new CustomEvent(what, object);
        return this._container.dispatchEvent(event);
    }
    /**
     * adds a listener, if accessable you can also use the container tag
     * read the javascript doc for further information
     */
    addEventListener(type, listener, options) {
        this._container.addEventListener(type, listener, options);
    }
    /**
     * removes a listener, if accessable you can also use the container tag
     * read the javascript doc for further information
     */
    removeEventListener(type, listener, options) {
        this._container.removeEventListener(type, listener, options);
    }
    /**
     * Set up the stage (cameras, scenes, viewports)
     * Call this before you call any other function.
     * @param setup composition of layout, camera, and scene. If not provided one scene, viewport and default camera is used.
     */
    setupStage(setup) {
        const cameras = setup.cameras;
        const [w, h] = this.getInnerSize();
        this._cameras.createFromDescriptions(w, h, ...cameras);
        const scenes = setup.scenes;
        for (const e of scenes) {
            this._createScene(e.name);
        }
        this._viewports.addFromDescriptions(...setup.viewports);
    }
    /**
     * returns the size of the canvas
     */
    getInnerSize() {
        return [
            this._container.clientWidth,
            this._container.clientHeight,
        ];
    }
    /**
     * If disabled, the dnd event is suppressed, and listeners doesn't get it.
     * Disable if Navigation should work at all times without risking interaction with an object.
     * Default is false
     * @param on the state to be set
     */
    enableDragAndDrop(on) {
        this._controls.enableDragAndDrop(on);
    }
    /**
     * Activate viewport using (mouse?) 2d position relative to browser's viewport
     * @param clientX x coordinate relative to viewport
     * @param clientY y coordinate relative to viewport
     * @see https://developer.mozilla.org/en/docs/Web/API/Element/getBoundingClientRect
     */
    selectViewport(clientX, clientY) {
        if (this._controls == null) {
            return;
        }
        const rect = this._renderer.domElement.getBoundingClientRect();
        const mousex = clientX - rect.left;
        const mousey = clientY - rect.top;
        const vp = this._viewports.getByPosition(mousex, mousey, rect.width, rect.bottom);
        if (vp) {
            this._selectViewport(vp);
        }
    }
    /**
     * Returns a function which forwards mouse coordinates from a mouse event
     * to relative-to-the-viewport positions [(-1,-1)...(1,1)].
     * The top-left edge is (-1, -1) and the bottom-right edge is (1,1).
     */
    readRelMousePosByViewport(viewportName, fn) {
        return (event) => {
            const vp = this._viewports.getByName(viewportName);
            if (vp) {
                const { left, top, width, height } = this._renderer.domElement.getBoundingClientRect();
                const mousex = ((event.clientX - left - vp.getLeft(width)) / vp.getWidth(width)) * 2 - 1;
                const mousey = -((event.clientY - top - vp.getTop(height)) / vp.getHeight(height)) * 2 + 1;
                fn({ mousex, mousey });
            }
        };
    }
    _selectViewport(vp) {
        if (this._currentViewport !== vp) {
            this._currentViewport = vp;
            this._controls.selectFor(this.getCamera(vp.camera), this._getSceneOrDefault(vp.scene), vp.navigator);
            this.dispatchEvent(Events.VIEWPORTCHANGED, { detail: { scene: vp.scene, camera: vp.camera, name: vp.name } });
        }
    }
    _clearRecursive(obj) {
        while (obj.children.length > 0) {
            Objects.dispose(obj.children[0]);
            this._clearRecursive(obj.children[0]);
            obj.remove(obj.children[0]);
        }
    }
    /*private _disposeObject(obj: Object3D): void {
  
      if (obj.userData.disposer) {
        obj.userData.disposer();
      }
  
      // tslint:disable-next-line:no-any
      const matobj = obj as any;
      if (!!matobj.geometry) { matobj.geometry.dispose(); }
      if (!!matobj.texture) { matobj.texture.dispose(); }
      if (Array.isArray(matobj.material)) {
        (matobj.material as Array<Material>).forEach((m) => m.dispose());
      } else if (!!matobj.material) {
        matobj.material.dispose();
      }
    }*/
    _setupContainer(containerOrId) {
        if (typeof containerOrId === 'string') {
            let container = document.getElementById(containerOrId);
            if (container == null) {
                // tslint:disable-next-line:no-console
                console.warn('Stage: Container ' + containerOrId.toString() + ' not found! Using <body instead>');
                container = document.body;
            }
            this._container = container;
        }
        else {
            this._container = containerOrId;
        }
        this._container.style.lineHeight = '0';
        this._eventWatcher = new EventWatcher(this._container);
        this._controls = new Controls(this._renderer.domElement, this._eventWatcher);
        if (this._container.style.height === '') {
            this._container.style.height = '100vh';
        }
        this._container.appendChild(this._renderer.domElement);
        this._renderer.setOverlayElement(this._container);
        // this._renderer.domElement.addEventListener('mousedown', this._selectViewport.bind(this));
    }
    _initRenderer(configuration) {
        if (configuration == null) {
            configuration = new RenderConfig();
        }
        this._renderer = new Renderer(configuration);
    }
    _createScene(name) {
        const scene = new Scene(this);
        this._scenes.set(name, scene);
        return scene;
    }
    _getSceneOrDefault(name = 'default') {
        if (this._scenes.has(name)) {
            return this._scenes.get(name);
        }
        else {
            return this._scenes.values().next().value; // the first is the 'default'
        }
    }
    _renderViewport(viewport) {
        const scene = this._getSceneOrDefault(viewport.scene);
        const camera = this.getCamera(viewport.camera);
        const [stageWidth, stageHeight] = this.getInnerSize();
        const left = viewport.getLeft(stageWidth);
        const width = viewport.getWidth(stageWidth);
        const bottom = viewport.getBottom(stageHeight);
        const height = viewport.getHeight(stageHeight);
        this._renderer.setSize(stageWidth, stageHeight);
        this._renderer.setViewport(left, bottom, width, height);
        this._renderer.setScissor(left, bottom, width, height);
        this._renderer.setScissorTest(true);
        if (this._eventWatcher.hasListeners(Events.VIEWPORTUPDATED) && viewport.hasChanged()) {
            this.dispatchEvent(Events.VIEWPORTUPDATED, { detail: { stageWidth, stageHeight, left, bottom, width, height, viewport } });
        }
        if (viewport.bgcolor !== undefined) {
            this._renderer.autoClearColor = !viewport.noclear;
            this._renderer.setClearColor(viewport.bgcolor, viewport.alpha || 1);
        }
        this._handleCameraChanges(camera, width, height);
        this._renderer.renderAdvanced(scene, camera);
    }
    _handleCameraChanges(camera, width, height) {
        this._cameras.adjustCamera(camera, width, height);
        if (this._eventWatcher.hasListeners(Events.CAMERACHANGED) && this._cameras.hasChanged(camera)) {
            this.dispatchEvent(Events.CAMERACHANGED, { detail: { camera } });
        }
    }
    _render() {
        this._viewports.forEachViewport((viewport) => {
            this._renderViewport(viewport);
        }, true);
    }
    _updateActors() {
        let actor;
        for (let i = 0; i < this._actors.length;) {
            actor = this._actors[i];
            if (actor.isExpired()) {
                this._actors.splice(i, 1);
            }
            else {
                actor.update();
                i++;
            }
        }
    }
    _animate() {
        if (!this._lockRendering) {
            this._updateActors();
            this._controls.update();
            this._render();
            if (this._postRenderHook) {
                this._postRenderHook();
            }
        }
        // don't uses window.requestAnimationFrame because VR handling
        this._renderer.requestAnimationFrame(this._animate.bind(this));
    }
    _loadDefaultsIfRequired() {
        if (this._scenes.size === 0) {
            this._createScene('default');
        }
    }
}
//# sourceMappingURL=Stage.js.map